import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Link,
  LinearProgress,
  Stack
} from '@mui/material';
import {
  ExpandMore,
  Person,
  Schedule,
  DirectionsCar,
  Work,
  ContactPhone,
  Payment,
  Gavel,
  CheckCircle,
  Cancel,
  Warning,
  RadioButtonUnchecked
} from '@mui/icons-material';
import { analyzeProfileCompletion } from '../utils/ProfileCompletionTracker';

const ComprehensiveOnboardingView = ({ maidProfile, onboardingData }) => {
  const [expandedSection, setExpandedSection] = useState('personalInfo');
  
  // Analyze profile completion
  const profileAnalysis = maidProfile ? analyzeProfileCompletion(maidProfile) : null;

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? section : false);
  };

  // Helper function to format boolean values
  const formatBoolean = (value) => {
    if (value === true) return <Chip label="Yes" color="success" size="small" />;
    if (value === false) return <Chip label="No" color="error" size="small" />;
    return <Chip label="Not Specified" color="default" size="small" />;
  };

  // Helper function to format arrays
  const formatArray = (arr, emptyText = "None specified") => {
    if (!Array.isArray(arr) || arr.length === 0) return emptyText;
    return arr.join(', ');
  };

  // Helper function to format weekly availability
  const formatWeeklyAvailability = (availability) => {
    if (!availability || typeof availability !== 'object') return "Not specified";
    
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const availableDays = days.filter(day => availability[day]);
    
    if (availableDays.length === 0) return "No days selected";
    return availableDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ');
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "Not specified";
    return `$${amount}/hour`;
  };

  // Helper function to get display value
  const getDisplayValue = (value, fallback = "Not specified") => {
    if (value === null || value === undefined || value === '') return fallback;
    return value;
  };

  const sections = [
    {
      id: 'personalInfo',
      title: 'Personal Information',
      icon: <Person />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Full Legal Name:</strong> {getDisplayValue(onboardingData?.fullLegalName)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Date of Birth:</strong> {getDisplayValue(onboardingData?.dateOfBirth)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Email:</strong> {getDisplayValue(onboardingData?.email)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Phone:</strong> {getDisplayValue(onboardingData?.phoneNumber)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography><strong>Residential Address:</strong> {getDisplayValue(onboardingData?.residentialAddress)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'availability',
      title: 'Availability & Schedule',
      icon: <Schedule />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Weekly Availability:</strong></Typography>
            <Typography variant="body2" sx={{ ml: 2, mt: 0.5 }}>
              {formatWeeklyAvailability(onboardingData?.weeklyAvailability)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Preferred Time Slots:</strong> {formatArray(onboardingData?.preferredTimeSlots)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Hours Per Week:</strong> {getDisplayValue(onboardingData?.hoursPerWeek)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Notice Required:</strong> {getDisplayValue(onboardingData?.noticeRequired)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Emergency Available:</strong> {formatBoolean(onboardingData?.emergencyAvailable)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Weekend Available:</strong> {formatBoolean(onboardingData?.weekendAvailable)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'transportation',
      title: 'Transportation & Equipment',
      icon: <DirectionsCar />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Has Vehicle:</strong> {formatBoolean(onboardingData?.hasVehicle)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Vehicle Type:</strong> {getDisplayValue(onboardingData?.vehicleType)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Can Transport Equipment:</strong> {formatBoolean(onboardingData?.canTransportEquipment)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Has Cleaning Supplies:</strong> {formatBoolean(onboardingData?.hasCleaningSupplies)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Has Reliable Transportation:</strong> {formatBoolean(onboardingData?.hasReliableTransportation)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'experience',
      title: 'Experience & Services',
      icon: <Work />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Experience Level:</strong> {getDisplayValue(onboardingData?.experienceLevel)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Preferred Hourly Rate:</strong> {formatCurrency(onboardingData?.preferredHourlyRate)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography><strong>Experience Description:</strong></Typography>
            <Typography variant="body2" sx={{ ml: 2, mt: 0.5 }}>
              {getDisplayValue(onboardingData?.experienceDescription)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography><strong>Services Offered:</strong> {formatArray(onboardingData?.servicesOffered)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography><strong>Preferred Service Areas:</strong> {formatArray(onboardingData?.preferredServiceAreas)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Max Travel Distance:</strong> {getDisplayValue(onboardingData?.maxTravelDistance, "Not specified")} miles</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography><strong>Special Skills:</strong> {getDisplayValue(onboardingData?.specialSkills)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'preferences',
      title: 'Service Preferences',
      icon: <Work />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Client Type Preferences:</strong> {formatArray(onboardingData?.clientTypePreferences)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Comfortable with Pets:</strong> {formatBoolean(onboardingData?.comfortableWithPets)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Willing to Work in Teams:</strong> {formatBoolean(onboardingData?.willingToWorkInTeams)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Preferred Communication:</strong> {getDisplayValue(onboardingData?.preferredCommunicationMethod)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Receive SMS Notifications:</strong> {formatBoolean(onboardingData?.receiveSmsNotifications)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'emergencyContact',
      title: 'Emergency Contact',
      icon: <ContactPhone />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Name:</strong> {getDisplayValue(onboardingData?.emergencyContactName)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Relationship:</strong> {getDisplayValue(onboardingData?.emergencyContactRelationship)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Phone:</strong> {getDisplayValue(onboardingData?.emergencyContactPhone)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Alternate Phone:</strong> {getDisplayValue(onboardingData?.emergencyContactAltPhone)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography><strong>Address:</strong> {getDisplayValue(onboardingData?.emergencyContactAddress)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'payment',
      title: 'Payment & Tax Information',
      icon: <Payment />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Payment Method:</strong> {getDisplayValue(onboardingData?.paymentMethod)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Bank Name:</strong> {getDisplayValue(onboardingData?.bankName)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Account Type:</strong> {getDisplayValue(onboardingData?.accountType)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Tax Classification:</strong> {getDisplayValue(onboardingData?.taxClassification)}</Typography>
          </Grid>
        </Grid>
      )
    },
    {
      id: 'legal',
      title: 'Legal & Insurance',
      icon: <Gavel />,
      content: (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Work Authorized:</strong> {formatBoolean(onboardingData?.workAuthorized)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Background Check Consent:</strong> {formatBoolean(onboardingData?.backgroundCheckConsent)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Has Insurance:</strong> {formatBoolean(onboardingData?.hasInsurance)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Insurance Provider:</strong> {getDisplayValue(onboardingData?.insuranceProvider)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><strong>Policy Number:</strong> {getDisplayValue(onboardingData?.policyNumber)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {onboardingData?.idUploadLink && (
              <Typography>
                <strong>ID Document:</strong> 
                <Link href={onboardingData.idUploadLink} target="_blank" rel="noopener" sx={{ ml: 1 }}>
                  View Document
                </Link>
              </Typography>
            )}
            {onboardingData?.coiUploadLink && (
              <Typography>
                <strong>Insurance Certificate:</strong> 
                <Link href={onboardingData.coiUploadLink} target="_blank" rel="noopener" sx={{ ml: 1 }}>
                  View Document
                </Link>
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Legal Agreements</Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip 
                label="Terms of Service" 
                color={onboardingData?.termsOfService ? 'success' : 'error'}
                icon={onboardingData?.termsOfService ? <CheckCircle /> : <Cancel />}
                variant={onboardingData?.termsOfService ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="Privacy Policy" 
                color={onboardingData?.privacyPolicy ? 'success' : 'error'}
                icon={onboardingData?.privacyPolicy ? <CheckCircle /> : <Cancel />}
                variant={onboardingData?.privacyPolicy ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="Safety Guidelines" 
                color={onboardingData?.safetyGuidelines ? 'success' : 'error'}
                icon={onboardingData?.safetyGuidelines ? <CheckCircle /> : <Cancel />}
                variant={onboardingData?.safetyGuidelines ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="Quality Standards" 
                color={onboardingData?.qualityStandards ? 'success' : 'error'}
                icon={onboardingData?.qualityStandards ? <CheckCircle /> : <Cancel />}
                variant={onboardingData?.qualityStandards ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="Marketing Communications" 
                color={onboardingData?.marketingCommunications ? 'success' : 'default'}
                icon={onboardingData?.marketingCommunications ? <CheckCircle /> : <Warning />}
                variant={onboardingData?.marketingCommunications ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip 
                label="Referral Program" 
                color={onboardingData?.referralProgram ? 'success' : 'default'}
                icon={onboardingData?.referralProgram ? <CheckCircle /> : <Warning />}
                variant={onboardingData?.referralProgram ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
          </Grid>
        </Grid>
      )
    }
  ];

  if (!onboardingData) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No comprehensive onboarding data available for this technician.
        </Typography>
      </Paper>
    );
  }

  // Map section IDs to completion tracker sections
  const sectionMapping = {
    personalInfo: 'personalInfo',
    availability: 'availability',
    transportation: 'availability', // Transportation is part of availability section
    workPreferences: 'preferences',
    experience: 'experience',
    emergencyContact: 'preferences', // Emergency contact is part of preferences
    payment: 'payment',
    legal: 'legalInsurance'
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Comprehensive Onboarding Information
      </Typography>
      
      {/* Overall completion summary */}
      {profileAnalysis && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.light' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Overall Completion: {profileAnalysis.overall.completionPercentage}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={profileAnalysis.overall.completionPercentage}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary">
              {profileAnalysis.overall.completedSections} of {profileAnalysis.overall.totalSections} sections
            </Typography>
          </Stack>
        </Paper>
      )}
      
      {sections.map((section) => {
        const mappedSectionKey = sectionMapping[section.id];
        const sectionAnalysis = profileAnalysis?.sections?.[mappedSectionKey];
        
        return (
          <Accordion
            key={section.id}
            expanded={expandedSection === section.id}
            onChange={handleAccordionChange(section.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {section.icon}
                  <Typography variant="h6">{section.title}</Typography>
                </Box>
                
                {/* Completion indicator */}
                {sectionAnalysis && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    <LinearProgress
                      variant="determinate"
                      value={sectionAnalysis.completionPercentage}
                      sx={{ 
                        width: 60, 
                        height: 6, 
                        borderRadius: 3,
                        backgroundColor: 'grey.200'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                      {sectionAnalysis.completionPercentage}%
                    </Typography>
                    {sectionAnalysis.isComplete ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : sectionAnalysis.missingRequired.length > 0 ? (
                      <Warning color="error" fontSize="small" />
                    ) : (
                      <RadioButtonUnchecked color="warning" fontSize="small" />
                    )}
                  </Box>
                )}
              </Stack>
            </AccordionSummary>
            
            <AccordionDetails>
              {/* Show missing fields alert if any */}
              {sectionAnalysis && sectionAnalysis.missingRequired.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'error.light', border: '1px solid', borderColor: 'error.main' }}>
                  <Typography variant="body2" color="error.dark" gutterBottom>
                    <Warning sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Missing Required Fields ({sectionAnalysis.missingRequired.length}):
                  </Typography>
                  <Typography variant="body2" color="error.dark">
                    {sectionAnalysis.missingRequired.map(field => field.label).join(', ')}
                  </Typography>
                </Paper>
              )}
              
              {section.content}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ComprehensiveOnboardingView;